/**
 * Barcode symbology tables: ZPL command → canonical type → bwip-js encoder id,
 * plus the data constraints used to warn about payloads the printer rejects.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/barcode-validator.js
 * Changes: ZPL parts only. The EPL tables, the ZPL serializer templates and
 * resolveOptimalType (designer-only type substitution) are not ported; a preview
 * must show the symbology the template asks for, so we warn instead of switching.
 */

import type { BarcodeType } from './document-model';

/** ZPL barcode command (two letters after ^) → canonical type. */
export const ZPL_CMD_TO_BARCODE: Readonly<Record<string, BarcodeType>> = {
  BC: 'code128',
  B3: 'code39',
  BA: 'code93',
  BE: 'ean13',
  B8: 'ean8',
  BU: 'upca',
  B9: 'upce',
  B2: 'itf',
  BK: 'codabar',
  BL: 'logmars',
  BM: 'msi',
  BP: 'plessey',
  BR: 'gs1databar',
  BZ: 'postnet',
  B5: 'planetcode',
  BI: 'industrial25',
  BJ: 'standard25',
  BB: 'codablockf',
  BQ: 'qrcode',
  BX: 'datamatrix',
  B7: 'pdf417',
  BF: 'micropdf417',
  B0: 'aztec',
  BO: 'aztec',
  BD: 'maxicode',
  B4: 'code49',
};

/** Canonical type → bwip-js encoder id. */
export const BWIP_ENCODER: Readonly<Record<BarcodeType, string>> = {
  code128: 'code128',
  code39: 'code39',
  code93: 'code93',
  ean13: 'ean13',
  ean8: 'ean8',
  upca: 'upca',
  upce: 'upce',
  itf: 'interleaved2of5',
  codabar: 'rationalizedCodabar',
  logmars: 'code39',
  msi: 'msi',
  plessey: 'plessey',
  gs1databar: 'databaromni',
  postnet: 'postnet',
  planetcode: 'planet',
  industrial25: 'industrial2of5',
  standard25: 'code2of5',
  codablockf: 'codablockf',
  qrcode: 'qrcode',
  datamatrix: 'datamatrix',
  pdf417: 'pdf417',
  micropdf417: 'micropdf417',
  aztec: 'azteccode',
  maxicode: 'maxicode',
  code49: 'code49',
};

/** Symbologies drawn as a square module grid (size = modules × magnification). */
export const MATRIX_TYPES: ReadonlySet<BarcodeType> = new Set<BarcodeType>(['qrcode', 'datamatrix', 'aztec']);

/** Row-based symbologies (height comes from rows × row height). */
export const STACKED_TYPES: ReadonlySet<BarcodeType> = new Set<BarcodeType>([
  'pdf417',
  'micropdf417',
  'codablockf',
  'code49',
]);

/**
 * Zebra draws a ^BQ symbol lower than its ^FO by the ^BY bar height in effect
 * at that field (10 dots at power-on). Measured against Labelary on all 29 QR
 * codes in the SV-551 reference set (QA gate 1, finding F3); pending
 * confirmation on a physical printer.
 */
export function zebraQrTopOffsetDots(barcodeHeightDots: number): number {
  return barcodeHeightDots;
}

/**
 * True when the printer still draws a symbol for an empty ^FD. Code 128: a
 * ZD421 at 203 dpi prints the start (B), check and stop patterns, 35 modules
 * (first paper print, SV-551-7.1). Every other symbology is conservatively
 * treated as printing nothing until a print shows otherwise.
 */
export function printsEmptySymbol(type: BarcodeType): boolean {
  return type === 'code128';
}

/** Symbologies the preview has been checked against Phoenix's templates for. */
export const VERIFIED_TYPES: ReadonlySet<BarcodeType> = new Set<BarcodeType>(['code128', 'qrcode']);

interface DataConstraint {
  pattern?: RegExp;
  maxLen?: number;
  evenLength?: boolean;
}

const DATA_CONSTRAINTS: Partial<Record<BarcodeType, DataConstraint>> = {
  ean13: { pattern: /^\d{12,13}$/ },
  ean8: { pattern: /^\d{7,8}$/ },
  upca: { pattern: /^\d{11,12}$/ },
  upce: { pattern: /^\d{6,8}$/ },
  itf: { pattern: /^\d+$/, evenLength: true },
  postnet: { pattern: /^\d{5}(\d{4})?(\d{2})?$/ },
  code39: { pattern: /^[A-Z0-9 \-.$/+%*]+$/i },
  logmars: { pattern: /^[A-Z0-9 \-.$/+%]+$/ },
  codabar: { pattern: /^[A-Da-d][0-9\-$:/.+]+[A-Da-d]$/ },
  qrcode: { maxLen: 4296 },
  datamatrix: { maxLen: 3116 },
  pdf417: { maxLen: 1850 },
  aztec: { maxLen: 3067 },
};

/** Returns a reason string when the data can't be encoded as this symbology, else null. */
export function checkBarcodeData(type: BarcodeType, data: string): string | null {
  const c = DATA_CONSTRAINTS[type];
  if (!c) return null;
  if (c.maxLen !== undefined && data.length > c.maxLen) return `data is longer than ${c.maxLen} characters`;
  if (c.pattern && !c.pattern.test(data)) return 'data has characters or a length this symbology does not allow';
  if (c.evenLength && data.length % 2 !== 0) return 'data must have an even number of digits';
  return null;
}

export const BARCODE_DISPLAY_NAME: Readonly<Record<BarcodeType, string>> = {
  code128: 'Code 128',
  code39: 'Code 39',
  code93: 'Code 93',
  ean13: 'EAN-13',
  ean8: 'EAN-8',
  upca: 'UPC-A',
  upce: 'UPC-E',
  itf: 'Interleaved 2 of 5',
  codabar: 'Codabar',
  logmars: 'LOGMARS',
  msi: 'MSI',
  plessey: 'Plessey',
  gs1databar: 'GS1 DataBar',
  postnet: 'POSTNET',
  planetcode: 'PLANET',
  industrial25: 'Industrial 2 of 5',
  standard25: 'Standard 2 of 5',
  codablockf: 'Codablock F',
  qrcode: 'QR Code',
  datamatrix: 'Data Matrix',
  pdf417: 'PDF417',
  micropdf417: 'MicroPDF417',
  aztec: 'Aztec',
  maxicode: 'MaxiCode',
  code49: 'Code 49',
};
